import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Filter, X } from 'lucide-react'
import { useThreadsWithMessages, useMessages } from '@/hooks/useChats'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { PageLoading } from '@/components/shared/Loading'
import { ChatTimeline } from '@/features/chats/ChatTimeline'
import { MessageBubble } from '@/features/chats/MessageBubble'
import { ThreadBoundary } from '@/features/chats/ThreadBoundary'
import { ThreadAnalysis } from '@/features/analysis/ThreadAnalysis'
import { Pagination } from '@/components/shared/Pagination'

export function ChatViewerPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedThreadId = searchParams.get('thread')
  const [messagePage, setMessagePage] = useState(1)

  // Fetch all threads for the sidebar/filter
  const { threads, loading: threadsLoading, error: threadsError } = useThreadsWithMessages(userId!)

  // If a specific thread is selected, fetch its messages with pagination
  const {
    thread: selectedThread,
    messages: selectedMessages,
    pagination: messagesPagination,
    loading: messagesLoading
  } = useMessages(
    userId!,
    selectedThreadId ? parseInt(selectedThreadId) : 0,
    messagePage,
    100
  )

  const handleThreadSelect = (threadId: string) => {
    if (threadId === 'all') {
      searchParams.delete('thread')
    } else {
      searchParams.set('thread', threadId)
    }
    setSearchParams(searchParams)
    setMessagePage(1)
  }

  const clearFilter = () => {
    searchParams.delete('thread')
    setSearchParams(searchParams)
  }

  // Show loading for initial thread fetch
  if (threadsLoading) return <PageLoading />

  if (threadsError) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">{threadsError}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          뒤로
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            뒤로
          </Button>
          <h1 className="text-2xl font-bold">채팅 기록</h1>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select
            value={selectedThreadId ?? 'all'}
            onChange={(e) => handleThreadSelect(e.target.value)}
          >
            <option value="all">모든 스레드</option>
            {threads.map((thread) => (
              <option key={thread.thread_id} value={thread.thread_id.toString()}>
                스레드 #{thread.thread_id} ({thread.message_count}개 메시지)
              </option>
            ))}
          </Select>
          {selectedThreadId && (
            <Button variant="ghost" size="sm" onClick={clearFilter}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Display selected thread or all threads */}
      {selectedThreadId && selectedThread ? (
        <div className="space-y-4">
          <ThreadBoundary
            threadId={selectedThread.id}
            startedAt={selectedThread.created_at}
            endedAt={selectedThread.ended_at}
            messageCount={selectedThread.message_count}
            isActive={selectedThread.is_active}
          />

          {messagesLoading ? (
            <PageLoading />
          ) : (
            <>
              <div className="space-y-2 border-l-2 border-gray-200 pl-4">
                {selectedMessages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    role={message.role}
                    content={message.content}
                    timestamp={message.timestamp}
                  />
                ))}
              </div>

              {messagesPagination && messagesPagination.total_pages > 1 && (
                <Pagination
                  current={messagesPagination.page}
                  total={messagesPagination.total_pages}
                  onChange={setMessagePage}
                />
              )}

              <ThreadAnalysis threadId={parseInt(selectedThreadId)} />
            </>
          )}
        </div>
      ) : (
        <ChatTimeline threads={threads} />
      )}
    </div>
  )
}
